import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line, CartesianGrid } from "recharts";
import { Activity, AlertTriangle, Clock, PackageSearch, CheckCircle2, XCircle, Star, ListChecks } from "lucide-react";
import { Link } from "react-router-dom";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/lib/AuthContext";
import { BrandLogo, SOFTWARE_NAME } from "@/lib/brand";

const COLORS = ["#0f172a","#2563eb","#10b981","#f59e0b","#ef4444","#8b5cf6","#06b6d4","#ec4899","#84cc16"];

function Kpi({ icon: Icon, label, value, accent, testId, sub }) {
  return (
    <div className="kpi-card" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.08em] text-slate-500 font-semibold">{label}</div>
          <div className="font-heading text-3xl font-bold text-slate-900 mt-1">{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
        <div className={`w-9 h-9 rounded-md flex items-center justify-center ${accent}`}>
          <Icon size={18} />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const readOnly = user?.role === "sales";
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data));
    api.get("/complaints?limit=10").then((r) => setRecent(r.data));
  }, []);

  if (!data) return <div className="text-sm text-slate-500">Loading dashboard...</div>;

  const k = data.kpis;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between">
        <div className="flex items-center gap-3">
          <BrandLogo size={48} testId="dashboard-brand-logo" />
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-900">Operations Dashboard</h1>
            <p className="text-sm text-slate-500 mt-1" data-testid="dashboard-software-name">{SOFTWARE_NAME} · live snapshot of the after-sales pipeline.</p>
          </div>
        </div>
        {!readOnly && (
          <Link to="/complaints/new" className="text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800 px-4 py-2 rounded-md" data-testid="dashboard-new-complaint-btn">+ New Complaint</Link>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi testId="kpi-total" icon={ListChecks} label="Total Complaints" value={k.total_complaints} accent="bg-slate-900 text-white" />
        <Kpi testId="kpi-open-today" icon={Activity} label="Open Today" value={k.open_today} accent="bg-blue-100 text-blue-700" />
        <Kpi testId="kpi-needing-solution" icon={AlertTriangle} label="Need Solution" value={k.needing_solution} accent="bg-orange-100 text-orange-700" />
        <Kpi testId="kpi-csat" icon={Star} label="CSAT (avg)" value={k.csat_avg || "—"} sub={`${k.csat_count} responses`} accent="bg-emerald-100 text-emerald-700" />
        <Kpi testId="kpi-assigned" icon={Activity} label="Assigned to Tech" value={k.assigned_to_tech} accent="bg-cyan-100 text-cyan-700" />
        <Kpi testId="kpi-pending" icon={Clock} label="Pending" value={k.pending} accent="bg-yellow-100 text-yellow-700" />
        <Kpi testId="kpi-parts-pending" icon={PackageSearch} label="Parts Pending" value={k.parts_pending} accent="bg-orange-100 text-orange-700" />
        <Kpi testId="kpi-pna" icon={AlertTriangle} label="PNA Cases" value={k.pna_cases} accent="bg-rose-100 text-rose-700" />
        <Kpi testId="kpi-in-transit" icon={Activity} label="Parts in Transit" value={k.parts_in_transit} accent="bg-amber-100 text-amber-700" />
        <Kpi testId="kpi-request-close" icon={CheckCircle2} label="Request to Close" value={k.request_to_close} accent="bg-sky-100 text-sky-700" />
        <Kpi testId="kpi-closed" icon={CheckCircle2} label="Closed" value={k.closed} accent="bg-emerald-100 text-emerald-700" />
        <Kpi testId="kpi-cancelled" icon={XCircle} label="Cancelled / Exchange / SR" value={(k.cancelled||0) + (k.exchange||0) + (k.sales_return||0)} accent="bg-slate-100 text-slate-700" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card data-testid="chart-by-brand">
          <CardContent className="p-4">
            <div className="flex items-baseline justify-between mb-3">
              <div className="font-heading font-semibold text-slate-900">Complaints by Brand</div>
              <span className="text-xs text-slate-500">Total: {data.by_brand.reduce((a, b) => a + b.count, 0)}</span>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.by_brand}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#0f172a" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card data-testid="chart-by-warranty">
          <CardContent className="p-4">
            <div className="font-heading font-semibold text-slate-900 mb-3">By Warranty Status</div>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={data.by_warranty} dataKey="count" nameKey="name" outerRadius={80} label>
                  {data.by_warranty.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card data-testid="chart-ageing">
          <CardContent className="p-4">
            <div className="font-heading font-semibold text-slate-900 mb-3">Ageing Buckets (Open)</div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.ageing_buckets}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#ef4444" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-2 text-xs text-slate-500">Avg first-visit: <span className="font-mono font-semibold text-slate-900">{k.avg_first_visit_ageing} d</span> · Avg E2E: <span className="font-mono font-semibold text-slate-900">{k.avg_e2e_ageing} d</span></div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card data-testid="chart-monthly">
          <CardContent className="p-4">
            <div className="font-heading font-semibold text-slate-900 mb-3">Monthly Trend</div>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={data.monthly_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="registered" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="closed" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card data-testid="chart-technicians">
          <CardContent className="p-4">
            <div className="font-heading font-semibold text-slate-900 mb-3">Top Technicians (Workload)</div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.by_technician} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} />
                <Tooltip />
                <Bar dataKey="count" fill="#0f172a" radius={[0,4,4,0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card data-testid="recent-complaints">
        <CardContent className="p-0">
          <div className="flex items-center justify-between p-4 border-b">
            <div className="font-heading font-semibold text-slate-900">Recent Complaints</div>
            <Link to="/complaints" className="text-xs font-semibold text-blue-600 hover:text-blue-700">View all →</Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full dense-table">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Complaint #</th>
                  <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Customer</th>
                  <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Brand / Product</th>
                  <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Technician</th>
                  <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Status</th>
                  <th className="text-right text-xs uppercase tracking-wide text-slate-600 font-semibold">Ageing</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((c) => (
                  <tr key={c.id} data-testid={`recent-row-${c.complaint_number}`}>
                    <td><Link to={`/complaints/${c.id}`} className="font-mono text-slate-900 font-medium hover:underline">{c.complaint_number}</Link></td>
                    <td>{c.customer_name} <div className="text-xs text-slate-500">{c.phone_number_1}</div></td>
                    <td>{c.brand}<div className="text-xs text-slate-500">{c.product_name}</div></td>
                    <td>{c.technician_name || <span className="text-slate-400">—</span>}</td>
                    <td><StatusBadge status={c.status} /></td>
                    <td className="text-right font-mono">{c.ageing_days != null ? `${c.ageing_days}d` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
